export const CAPABILITIES = {
  DASHBOARD: {
    VIEW: "dashboard.view.basic",
    MANAGE: "dashboard.manage"
  },
  ASSETS: {
    VIEW: "assets.view",
    MANAGE: "assets.manage",
    HISTORY_VIEW: "assets.history.view"
  },
  ASSET_SEARCH: {
    RUN: "asset_search.run",
    EXPORT: "asset_search.export",
    SEARCH_ALL_WORKSPACES: "asset_search.search_all_workspaces",
    SAVED_VIEW: "asset_search.saved.view",
    SAVED_CREATE: "asset_search.saved.create",
    SAVED_UPDATE: "asset_search.saved.update",
    SAVED_DELETE: "asset_search.saved.delete",
    SAVED_MANAGE_WORKSPACE: "asset_search.saved.manage_workspace",
    SAVED_MANAGE_COMPANY: "asset_search.saved.manage_company",
    PINNED_VIEW: "asset_search.pinned.view",
    PINNED_CREATE: "asset_search.pinned.create",
    PINNED_UPDATE: "asset_search.pinned.update",
    PINNED_DELETE: "asset_search.pinned.delete"
  },
  BULK_OPERATIONS: {
    VIEW: "bulk_operations.view"
  },
  FORMS: {
    VIEW: "forms.view",
    EXECUTE: "forms.execute",
    MANAGE: "forms.manage",
    CREATE: "forms.create",
    UPDATE: "forms.update",
    ARCHIVE: "forms.archive"
  },
  FORM_RESPONSES: {
    VIEW: "form_responses.view",
    MANAGE: "form_responses.manage",
    REVIEW: "form_responses.review",
    EXPORT: "form_responses.export"
  },
  MAINTENANCE: {
    VIEW: "maintenance.view",
    CREATE_PLAN: "maintenance.create_plan",
    UPDATE_PLAN: "maintenance.update_plan",
    PAUSE_PLAN: "maintenance.pause_plan",
    RUN_NOW: "maintenance.run_now",
    VIEW_HISTORY: "maintenance.view_history"
  },
  PHOTO_MANAGER: {
    VIEW: "photo_manager.view",
    MANAGE: "photo_manager.manage"
  },
  WORK_ORDERS: {
    VIEW: "work_orders.view",
    CREATE: "work_orders.create",
    EXECUTE: "work_orders.execute",
    APPROVE: "work_orders.approve",
    UNDO_APPROVE: "work_orders.undo_approve",
    TRANSFER_EXECUTION: "work_orders.transfer_execution",
    DELETE: "work_orders.delete",
    VIEW_OWN: "work_orders.view_own",
    VIEW_TEAM: "work_orders.view_team",
    VIEW_ALL: "work_orders.view_all",
    VIEW_POOL: "work_orders.view_pool",
    TAKE_FROM_POOL: "work_orders.take_from_pool",
    RELEASE_TO_POOL: "work_orders.release_to_pool"
  },
  SCHEDULER: {
    VIEW: "scheduler.view",
    SCHEDULE: "scheduler.schedule",
    RESCHEDULE: "scheduler.reschedule",
    REASSIGN: "scheduler.reassign",
    UNSCHEDULE: "scheduler.unschedule",
    MANAGE_SETTINGS: "scheduler.manage_settings"
  },
  USERS: {
    DIRECTORY_VIEW: "users.directory.view"
  },
  JOB_TYPES: {
    MANAGE: "job_types.manage"
  },
  APPROVAL_FLOWS: {
    VIEW: "approval_flows.view",
    MANAGE: "approval_flows.manage"
  },
  WORK_ORDER_APPROVALS: {
    VIEW: "work_order_approvals.view",
    REVIEW_REPORT: "work_order_approvals.review_report",
    REVIEW_INVENTORY: "work_order_approvals.review_inventory"
  },
  TEAMS: {
    VIEW: "teams.view",
    CREATE: "teams.create",
    UPDATE: "teams.update",
    ARCHIVE: "teams.archive",
    MANAGE_MEMBERS: "teams.manage_members"
  },
  PROJECTS: {
    VIEW: "projects.view",
    CREATE: "projects.create",
    UPDATE: "projects.update",
    ARCHIVE: "projects.archive",
    MANAGE_ITEMS: "projects.manage_items"
  },
  VENDORS: {
    VIEW: "vendors.view"
  },
  INVENTORY: {
    VIEW: "inventory.view",
    IMPORT: "inventory.import",
    VIEW_TRANSACTIONS: "inventory.view_transactions",
    VIEW_ARCHIVED: "inventory.view_archived",
    MANAGE_WAREHOUSES: "inventory.manage_warehouses",
    RECEIVE: "inventory.receive",
    INSTALL: "inventory.install",
    CORRECT_SERIAL: "inventory.correct_serial",
    UNINSTALL: "inventory.uninstall",
    /** Incidents list/detail (sitehub-backend#3442 block C / #3513). */
    VIEW_INCIDENTS: "inventory.view_incidents"
  },
  MEDIA_TEMPLATES: {
    VIEW: "media_templates.view",
    MANAGE: "media_templates.manage"
  },
  MEDIA_EXECUTION: {
    EXECUTE: "media_execution.execute"
  },
  DOCUMENTS: {
    VIEW: "documents.view",
    UPLOAD: "documents.upload",
    UPLOAD_VERSION: "documents.upload_version",
    UPDATE: "documents.update",
    ARCHIVE: "documents.archive",
    SHARE: "documents.share",
    TYPES_MANAGE: "document_types.manage"
  },
  FORM_ANALYSIS: {
    VIEW: "form_analysis.view",
    SAVED_MANAGE: "form_analysis.saved.manage",
    JOBS_VIEW: "form_analysis.jobs.view",
    EXPORT: "form_analysis.export",
    REPORT_SUMMARY: "form_analysis.report.summary",
    ANALYZE_ALL_WORKSPACES: "form_analysis.analyze_all_workspaces"
  },
  TIMESHEETS: {
    VIEW: "timesheets.view",
    VIEW_TEAM_TIME: "timesheets.view_team_time",
    VIEW_ALL_WORKSPACES: "timesheets.view_all_workspaces",
    MANAGE: "timesheets.manage",
    APPROVE: "timesheets.approve",
    REQUEST_CORRECTION: "timesheets.request_correction",
    EXPORT: "timesheets.export",
    MANAGE_SETTINGS: "timesheets.manage_settings",
    MANAGE_KIOSK: "timesheets.manage_kiosk",
    MANAGE_PIN: "timesheets.manage_pin"
  }
} as const;

export type CapabilityContractFallback = "allow" | "deny";

function normalizeCapability(capability: string): string {
  return capability.trim().toLowerCase();
}

export function hasAnyCapability(
  userCapabilities: readonly string[] | null | undefined,
  capabilities: readonly string[]
): boolean {
  if (!userCapabilities || capabilities.length === 0) return false;

  const normalized = new Set(userCapabilities.map(normalizeCapability));
  return capabilities.some((capability) =>
    normalized.has(normalizeCapability(capability))
  );
}

export function resolveCapabilityAccess(params: {
  userCapabilities: readonly string[] | null | undefined;
  capabilities: readonly string[];
  whenContractMissing?: CapabilityContractFallback;
}): boolean {
  if (
    params.userCapabilities === undefined ||
    params.userCapabilities === null
  ) {
    return params.whenContractMissing === "allow";
  }

  return hasAnyCapability(params.userCapabilities, params.capabilities);
}

export function hasConfiguredModule(params: {
  effectiveModules?: readonly string[] | null;
  companyModules?: readonly string[] | null;
  modules: readonly string[];
}): boolean {
  const available = params.effectiveModules ?? params.companyModules;
  if (!available || params.modules.length === 0) return false;
  const normalized = new Set(available.map(normalizeCapability));
  return params.modules.some((moduleName) =>
    normalized.has(normalizeCapability(moduleName))
  );
}

export type CanonicalAccessControl = {
  schema_version?: string;
  profile_status?: string;
  capabilities?: string[];
  effective_modules?: string[];
  operational_workspace_ids?: number[];
  workspace_scope?: {
    operational_workspace_ids?: number[];
    can_access_master?: boolean;
  };
};

export function hasCanonicalCapability(
  accessControl: CanonicalAccessControl | null | undefined,
  ...requiredCapabilities: string[]
): boolean {
  if (!requiredCapabilities.length) return false;
  const effectiveCapabilities = accessControl?.capabilities ?? [];
  return requiredCapabilities.some((requiredCapability) =>
    effectiveCapabilities.includes(requiredCapability)
  );
}

export function hasCanonicalOperationalCapability(
  accessControl: CanonicalAccessControl | null | undefined,
  workspaceId: number | string | null | undefined,
  ...requiredCapabilities: string[]
): boolean {
  if (workspaceId == null) return false;
  const operationalWorkspaceIds =
    accessControl?.workspace_scope?.operational_workspace_ids ??
    accessControl?.operational_workspace_ids ??
    [];
  const workspaceAllowed = operationalWorkspaceIds.some(
    (allowedId) => String(allowedId) === String(workspaceId)
  );
  return (
    workspaceAllowed &&
    hasCanonicalCapability(accessControl, ...requiredCapabilities)
  );
}
